import {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  type ClipboardEvent,
  type FocusEvent,
  type FormEvent,
  type ForwardedRef,
  type HTMLAttributes,
  type KeyboardEvent,
} from 'react';

/** Каретку в конец содержимого (для ВНЕШНЕй установки value — чтобы не прыгала в начало). */
function placeCaretAtEnd(el: HTMLElement) {
  const range = document.createRange();
  range.selectNodeContents(el);
  range.collapse(false);
  const sel = window.getSelection();
  sel?.removeAllRanges();
  sel?.addRange(range);
}

export interface PlainFieldProps {
  value: string;
  onChange: (value: string) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLDivElement>) => void;
  onFocus?: (e: FocusEvent<HTMLDivElement>) => void;
  onBlur?: (e: FocusEvent<HTMLDivElement>) => void;
  /** Фокус при монтировании (эмуляция нативного autoFocus для contenteditable). */
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  /** Класс-консьюмер задаёт коробку/шрифт/каретку (.text-input, .ml-input, …). */
  className?: string;
  inputMode?: HTMLAttributes<HTMLDivElement>['inputMode'];
  autoCapitalize?: string;
  lang?: string;
  spellCheck?: boolean;
  ariaLabel?: string;
  /** Предел длины, как `maxLength` у нативного input: лишнее просто не вводится
   *  (и обрезается при вставке). У contenteditable своего атрибута нет. */
  maxLength?: number;
  /** id поля — на него ссылаются соседние подписи. */
  id?: string;
  /** Подпись клавиши Enter на экранной клавиатуре (`go`, `done`…). */
  enterKeyHint?: HTMLAttributes<HTMLDivElement>['enterKeyHint'];
  /** Автозамена iOS: 'off' — английское слово не «исправляется» при наборе. */
  autoCorrect?: 'on' | 'off';
  /** Поле открывает лист или список (строка создания на главной, ТЗ § 16). */
  ariaExpanded?: boolean;
  ariaControls?: string;
  ariaDescribedBy?: string;
}

/**
 * Однострочное текстовое поле на `contenteditable` вместо нативного `<input>`.
 *
 * Зачем: системное автозаполнение Chrome/Android (панель 🔑 пароли / 💳 карты /
 * 📍 адреса над клавиатурой) цепляется ТОЛЬКО к нативным form-контролам
 * (input/textarea/select) и игнорирует `autocomplete="off"` (Google не даёт
 * сайтам отключать менеджер паролей). К `contenteditable`-элементу панель не
 * привязывается — поэтому поля ввода (кроме логина/пароля, где менеджер нужен)
 * рендерятся этим компонентом.
 *
 * Снаружи ведёт себя как контролируемый `<input>`: `value`/`onChange`, Enter
 * отдаётся в `onKeyDown` (перенос строки подавлен — поле однострочное), вставка
 * нормализуется в plain-text. Коробку/шрифт/каретку даёт `className`-консьюмер;
 * плейсхолдер рисуется через CSS `.pf:empty::before` (у contenteditable нет
 * `::placeholder`).
 */
function PlainFieldImpl(
  {
    value,
    onChange,
    onKeyDown,
    onFocus,
    onBlur,
    autoFocus = false,
    placeholder,
    disabled = false,
    className,
    inputMode = 'text',
    autoCapitalize = 'off',
    lang,
    spellCheck = false,
    ariaLabel,
    maxLength,
    id,
    enterKeyHint,
    autoCorrect,
    ariaExpanded,
    ariaControls,
    ariaDescribedBy,
  }: PlainFieldProps,
  forwardedRef: ForwardedRef<HTMLDivElement>,
) {
  const innerRef = useRef<HTMLDivElement | null>(null);
  // Последнее значение, которое МЫ отдали через onChange. Нужен, чтобы отличить
  // «эхо» нашего же ввода (DOM уже актуален — трогать нельзя) от ВНЕШНЕГО
  // изменения value (сброс на новой карточке / программная установка).
  const lastEmitted = useRef('');

  // Свой ref для синхронизации DOM + проброс наружу (движок зовёт .focus()).
  const setRefs = useCallback(
    (node: HTMLDivElement | null) => {
      innerRef.current = node;
      if (typeof forwardedRef === 'function') forwardedRef(node);
      else if (forwardedRef) forwardedRef.current = node;
    },
    [forwardedRef],
  );

  // Контролируемое значение поверх contenteditable.
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    // value === эхо нашего же ввода → DOM уже актуален, НИЧЕГО не пишем. Иначе
    // перетёрли бы символ, набранный в зазоре между рендером и flush этого
    // эффекта (потеря буквы + прыжок каретки при быстром наборе).
    if (value === lastEmitted.current) return;
    // Внешнее изменение value (сброс на новой карточке / программная установка).
    if (value === '') {
      // Жёстко очищаем innerHTML (убирает возможный <br>), чтобы показался
      // `:empty`-плейсхолдер.
      if (el.innerHTML !== '') el.innerHTML = '';
    } else if (el.textContent !== value) {
      el.textContent = value;
      if (document.activeElement === el) placeCaretAtEnd(el);
    }
    lastEmitted.current = value;
  }, [value]);

  // Эмуляция нативного autoFocus (у contenteditable его нет).
  useEffect(() => {
    if (autoFocus) innerRef.current?.focus();
    // один раз при монтировании
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emit = (el: HTMLDivElement) => {
    const raw = (el.textContent ?? '').replace(/\r?\n/g, '');
    // Перебор гасим прямо в DOM: иначе контролируемое value вернулось бы
    // обрезанным, а в поле осталось бы лишнее — и каретка прыгала бы в начало.
    const text = maxLength != null && raw.length > maxLength ? raw.slice(0, maxLength) : raw;
    if (text !== raw) {
      el.textContent = text;
      placeCaretAtEnd(el);
    }
    if (text === '' && el.innerHTML !== '') el.innerHTML = '';
    lastEmitted.current = text;
    onChange(text);
  };

  const handleInput = (e: FormEvent<HTMLDivElement>) => emit(e.currentTarget);

  const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') e.preventDefault(); // однострочное: без переноса
    onKeyDown?.(e);
  };

  const handlePaste = (e: ClipboardEvent<HTMLDivElement>) => {
    // Вставляем как простой текст и схлопываем пробелы/переносы (поле однострочное).
    e.preventDefault();
    const pasted = e.clipboardData.getData('text/plain').replace(/\s+/g, ' ').trim();
    const room =
      maxLength == null
        ? pasted.length
        : Math.max(0, maxLength - (e.currentTarget.textContent ?? '').length);
    const text = pasted.slice(0, room);
    if (!text) return;
    document.execCommand('insertText', false, text);
  };

  return (
    <div
      ref={setRefs}
      className={['pf', className].filter(Boolean).join(' ')}
      contentEditable={!disabled}
      suppressContentEditableWarning
      // contenteditable фокусируется и так, но явный tabIndex держит поле в
      // tab-order и в селекторных focus-trap'ах модалок ([tabindex]). Отключённое — без tabIndex вовсе: -1 убирал
      // поле только из Tab, а кликом оно всё равно получало фокус (заблокированная строка главной подсвечивалась,
      // владелец 2026-10-23: «почему фокус срабатывает, если заблокировано»).
      tabIndex={disabled ? undefined : 0}
      id={id}
      role="textbox"
      aria-multiline={false}
      aria-label={ariaLabel ?? placeholder}
      aria-disabled={disabled || undefined}
      aria-expanded={ariaExpanded}
      aria-controls={ariaControls}
      aria-describedby={ariaDescribedBy}
      enterKeyHint={enterKeyHint}
      autoCorrect={autoCorrect}
      data-placeholder={placeholder}
      inputMode={inputMode}
      autoCapitalize={autoCapitalize}
      spellCheck={spellCheck}
      lang={lang}
      onInput={handleInput}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      onFocus={onFocus}
      onBlur={onBlur}
    />
  );
}

export const PlainField = forwardRef(PlainFieldImpl);
